import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import {
    Upload,
    FileText,
    Image as ImageIcon,
    X,
    Trash2,
    Calendar,
    DollarSign,
    Building2,
    FileUp,
    CheckCircle2,
    Eye,
    Receipt,
    Loader2,
    Edit3,
    Save,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    Search,
    Plus,
    Download
} from "lucide-react";

import "./ReceiptUpload.css";

const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:5000";

const ReceiptUpload = () => {
    const fileInputRef = useRef(null);

    /* =========================================================
       UPLOAD FILES
    ========================================================= */

    const [files, setFiles] = useState([]);

    const [dragActive, setDragActive] = useState(false);

    const [uploading, setUploading] = useState(false);

    /* =========================================================
       RECEIPTS FROM DATABASE
    ========================================================= */

    const [receipts, setReceipts] = useState([]);

    const [loadingReceipts, setLoadingReceipts] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");

    const [filterDateFrom, setFilterDateFrom] = useState("");

    const [filterDateTo, setFilterDateTo] = useState("");

    const [showAllReceipts, setShowAllReceipts] = useState(false);

    /* =========================================================
       EDIT
    ========================================================= */

    const [editingReceipt, setEditingReceipt] = useState(null);

    const [savingEdit, setSavingEdit] = useState(false);

    /* =========================================================
       VIEW MODAL
    ========================================================= */

    const [viewingReceipt, setViewingReceipt] = useState(null);

    /* =========================================================
       CURRENCY CONFIGURATION
    ========================================================= */

    const [baseCurrency, setBaseCurrency] = useState("KES");

    const currencyOptions = [
        { code: "KES", symbol: "KSh", name: "Kenyan Shilling", rate: 1 },
        { code: "USD", symbol: "$", name: "US Dollar", rate: 0.0077 },
        { code: "EUR", symbol: "€", name: "Euro", rate: 0.0071 },
        { code: "GBP", symbol: "£", name: "British Pound", rate: 0.0061 },
        { code: "UGX", symbol: "USh", name: "Ugandan Shilling", rate: 28.5 },
        { code: "TZS", symbol: "TSh", name: "Tanzanian Shilling", rate: 19.2 },
        { code: "RWF", symbol: "FRw", name: "Rwandan Franc", rate: 7.8 },
        { code: "ZAR", symbol: "R", name: "South African Rand", rate: 0.14 }
    ];

    const getCurrencySymbol = (code) => {
        const currency = currencyOptions.find(c => c.code === code);
        return currency ? currency.symbol : code;
    };

    /* =========================================================
       TOKEN
    ========================================================= */

    const getToken = () => {
        return (
            localStorage.getItem("token") ||
            localStorage.getItem("accessToken") ||
            localStorage.getItem("authToken")
        );
    };

    /* =========================================================
       AXIOS CONFIG
    ========================================================= */

    const authConfig = () => {
        const token = getToken();

        return {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };
    };

    /* =========================================================
       INITIALIZE
    ========================================================= */

    useEffect(() => {
        fetchReceipts();
    }, []);

    /* =========================================================
       FETCH RECEIPTS
    ========================================================= */

    const fetchReceipts = async () => {
        try {
            setLoadingReceipts(true);

            const response = await axios.get(
                `${API_BASE_URL}/api/receipts`,
                authConfig()
            );

            if (response.data?.success) {

                setReceipts(
                    Array.isArray(response.data.receipts)
                        ? response.data.receipts
                        : []
                );
            } else {
                setReceipts([]);
            }

        } catch (error) {
            console.error(
                "FETCH RECEIPTS ERROR:",
                error
            );

            setReceipts([]);

            if (error.response?.status === 401) {
                console.error(
                    "Authentication expired."
                );
            }

        } finally {
            setLoadingReceipts(false);
        }
    };

    /* =========================================================
       FILE VALIDATION
    ========================================================= */

    const validateFile = (file) => {
        if (!file) return false;

        const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
            "application/pdf"
        ];

        if (!allowedTypes.includes(file.type)) {
            alert(
                `${file.name} is not supported.\n\nPlease upload JPG, JPEG, PNG, WEBP or PDF.`
            );

            return false;
        }

        const maxSize =
            10 * 1024 * 1024;

        if (file.size > maxSize) {
            alert(
                `${file.name} is larger than 10MB.`
            );

            return false;
        }

        return true;
    };

    /* =========================================================
       CREATE FILE OBJECT
    ========================================================= */

    const createUploadFile = (file) => {
        return {
            id:
                Date.now() +
                Math.random()
                    .toString(36)
                    .substring(2),

            file,

            preview:
                file.type.startsWith("image/")
                    ? URL.createObjectURL(file)
                    : null,

            fileType:
                file.type.startsWith("image/")
                    ? "image"
                    : "pdf",

            supplier: "",

            receiptNumber: "",

            amount: "",

            receiptDate:
                new Date()
                    .toISOString()
                    .split("T")[0],

            description: "",

            currency: "KES"
        };
    };

    /* =========================================================
       ADD FILES
    ========================================================= */

    const addFiles = (selectedFiles) => {
        const incoming = Array.from(
            selectedFiles || []
        );

        if (!incoming.length) return;

        const validFiles =
            incoming.filter(validateFile);

        const newFiles =
            validFiles.map(
                createUploadFile
            );

        setFiles((prev) => [
            ...prev,
            ...newFiles
        ]);
    };

    /* =========================================================
       FILE INPUT
    ========================================================= */

    const handleFileInput = (e) => {
        addFiles(e.target.files);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    /* =========================================================
       DRAG EVENTS
    ========================================================= */

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setDragActive(false);

        addFiles(
            e.dataTransfer.files
        );
    };

    /* =========================================================
       UPDATE FILE METADATA
    ========================================================= */

    const updateFileMetadata = (
        id,
        field,
        value
    ) => {
        setFiles((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        [field]: value
                    }
                    : item
            )
        );
    };

    /* =========================================================
       REMOVE SELECTED FILE
    ========================================================= */

    const removeSelectedFile = (id) => {
        setFiles((prev) => {
            const fileToRemove =
                prev.find(
                    (item) =>
                        item.id === id
                );

            if (
                fileToRemove?.preview
            ) {
                URL.revokeObjectURL(
                    fileToRemove.preview
                );
            }

            return prev.filter(
                (item) =>
                    item.id !== id
            );
        });
    };

    /* =========================================================
       CLEAR ALL SELECTED FILES
    ========================================================= */

    const clearFiles = () => {
        files.forEach((item) => {
            if (item.preview) {
                URL.revokeObjectURL(
                    item.preview
                );
            }
        });

        setFiles([]);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    /* =========================================================
       VALIDATE RECEIPTS BEFORE UPLOAD
    ========================================================= */

    const validateUpload = () => {
        if (!files.length) {
            alert(
                "Please select at least one receipt."
            );

            return false;
        }

        for (let i = 0; i < files.length; i++) {
            const item = files[i];

            if (!item.supplier.trim()) {
                alert(
                    `Please enter the supplier for "${item.file.name}".`
                );

                return false;
            }

            if (
                item.amount !== "" &&
                Number(item.amount) < 0
            ) {
                alert(
                    `Invalid amount for "${item.file.name}".`
                );

                return false;
            }

            if (!item.receiptDate) {
                alert(
                    `Please select a date for "${item.file.name}".`
                );

                return false;
            }
        }

        return true;
    };

    /* =========================================================
       UPLOAD MULTIPLE RECEIPTS
    ========================================================= */

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateUpload()) {
            return;
        }

        setUploading(true);

        try {
            const uploadData =
                new FormData();

            files.forEach((item) => {
                uploadData.append(
                    "files",
                    item.file
                );
            });

            const metadata =
                files.map((item) => ({
                    supplier:
                        item.supplier.trim(),

                    receiptNumber:
                        item.receiptNumber.trim(),

                    amount:
                        item.amount === ""
                            ? 0
                            : Number(
                                item.amount
                            ),

                    receiptDate:
                        item.receiptDate,

                    description:
                        item.description.trim(),

                    currency: item.currency || "KES"
                }));

            uploadData.append(
                "metadata",
                JSON.stringify(metadata)
            );

            const response =
                await axios.post(
                    `${API_BASE_URL}/api/receipts`,
                    uploadData,
                    {
                        headers: {
                            Authorization:
                                `Bearer ${getToken()}`
                        }
                    }
                );

            if (
                response.data?.success
            ) {
                alert(
                    `${files.length} receipt${files.length > 1 ? "s" : ""} uploaded successfully.`
                );

                clearFiles();

                await fetchReceipts();

            } else {
                throw new Error(
                    response.data?.message ||
                    "Upload failed"
                );
            }

        } catch (error) {
            console.error(
                "RECEIPT UPLOAD ERROR:",
                error
            );

            console.error(
                "SERVER RESPONSE:",
                error.response?.data
            );

            alert(
                error.response?.data
                    ?.message ||
                "Failed to upload receipts."
            );

        } finally {
            setUploading(false);
        }
    };

    /* =========================================================
       DELETE RECEIPT
    ========================================================= */

    const deleteReceipt = async (
        receipt
    ) => {
        const confirmed =
            window.confirm(
                `Are you sure you want to delete this receipt from ${receipt.supplier}?`
            );

        if (!confirmed) return;

        try {
            const response =
                await axios.delete(
                    `${API_BASE_URL}/api/receipts/${receipt.id}`,
                    authConfig()
                );

            if (
                response.data?.success
            ) {
                setReceipts((prev) =>
                    prev.filter(
                        (item) =>
                            item.id !==
                            receipt.id
                    )
                );

                if (
                    viewingReceipt?.id ===
                    receipt.id
                ) {
                    setViewingReceipt(null);
                }

                alert(
                    "Receipt deleted successfully."
                );

            } else {
                throw new Error(
                    response.data?.message ||
                    "Delete failed"
                );
            }

        } catch (error) {
            console.error(
                "DELETE RECEIPT ERROR:",
                error
            );

            alert(
                error.response?.data
                    ?.message ||
                "Failed to delete receipt."
            );
        }
    };

    /* =========================================================
       EDIT
    ========================================================= */

    const startEdit = (receipt) => {
        setEditingReceipt({
            ...receipt,

            receiptDate:
                normalizeDate(
                    receipt.receipt_date ||
                    receipt.receiptDate
                ),

            amount:
                receipt.amount ?? "",

            supplier:
                receipt.supplier || "",

            receiptNumber:
                receipt.receipt_number ||
                receipt.receiptNumber ||
                "",

            description:
                receipt.description || "",

            currency: receipt.currency || "KES"
        });
    };

    /* =========================================================
       NORMALIZE DATE
    ========================================================= */

    const normalizeDate = (
        value
    ) => {
        if (!value) return "";

        if (
            typeof value === "string"
        ) {
            return value.substring(
                0,
                10
            );
        }

        const date =
            new Date(value);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return "";
        }
        console.log(date);

        return date
            .toISOString()
            .substring(0, 10);
    };

    /* =========================================================
       SAVE EDIT
    ========================================================= */

    const saveEdit = async () => {
        if (!editingReceipt) {
            return;
        }

        if (
            !editingReceipt.supplier?.trim()
        ) {
            alert(
                "Supplier is required."
            );

            return;
        }

        setSavingEdit(true);

        try {
            const payload = {
                supplier:
                    editingReceipt.supplier.trim(),

                receiptNumber:
                    (
                        editingReceipt.receiptNumber ||
                        ""
                    ).trim(),

                amount:
                    Number(
                        editingReceipt.amount ||
                        0
                    ),

                receiptDate:
                    normalizeDate(
                        editingReceipt.receiptDate
                    ),

                description:
                    (
                        editingReceipt.description ||
                        ""
                    ).trim(),

                currency: editingReceipt.currency || "KES"
            };

            const response =
                await axios.put(
                    `${API_BASE_URL}/api/receipts/${editingReceipt.id}`,
                    payload,
                    authConfig()
                );

            if (
                response.data?.success
            ) {
                setEditingReceipt(
                    null
                );

                await fetchReceipts();

                alert(
                    "Receipt updated successfully."
                );

            } else {
                throw new Error(
                    response.data?.message ||
                    "Update failed"
                );
            }

        } catch (error) {
            console.error(
                "UPDATE RECEIPT ERROR:",
                error
            );

            console.error(
                "SERVER RESPONSE:",
                error.response?.data
            );

            alert(
                error.response?.data
                    ?.message ||
                "Failed to update receipt."
            );

        } finally {
            setSavingEdit(false);
        }
    };

    /* =========================================================
       RECEIPT FILE URL
    ========================================================= */

    const getReceiptFileUrl = (
        receipt
    ) => {
        const filePath =
            receipt.file_path ||
            receipt.filePath ||
            "";

        if (!filePath) {
            return null;
        }

        if (
            filePath.startsWith(
                "http://"
            ) ||
            filePath.startsWith(
                "https://"
            )
        ) {
            return filePath;
        }

        return `${API_BASE_URL}${filePath.startsWith("/") ? "" : "/"
            }${filePath}`;
    };

    /* =========================================================
       VIEW RECEIPT
    ========================================================= */

    const viewReceipt = (
        receipt
    ) => {
        setViewingReceipt(
            receipt
        );
    };

    /* =========================================================
       DOWNLOAD RECEIPT
    ========================================================= */

    const downloadReceipt = (
        receipt
    ) => {
        const url =
            getReceiptFileUrl(
                receipt
            );

        if (!url) {
            alert(
                "Receipt file is not available."
            );

            return;
        }

        window.open(
            url,
            "_blank",
            "noopener,noreferrer"
        );
    };

    /* =========================================================
       FILTER
    ========================================================= */

    const filteredReceipts =
        receipts.filter(
            (receipt) => {
                const search =
                    searchTerm
                        .trim()
                        .toLowerCase();

                const matchesSearch =
                    !search ||
                    String(
                        receipt.supplier ||
                        ""
                    )
                        .toLowerCase()
                        .includes(search) ||

                    String(
                        receipt.receipt_number ||
                        ""
                    )
                        .toLowerCase()
                        .includes(search) ||

                    String(
                        receipt.file_name ||
                        ""
                    )
                        .toLowerCase()
                        .includes(search) ||

                    String(
                        receipt.currency ||
                        ""
                    )
                        .toLowerCase()
                        .includes(search);

                const receiptDate =
                    normalizeDate(
                        receipt.receipt_date ||
                        receipt.receiptDate
                    );

                const matchesDateFrom =
                    !filterDateFrom ||
                    (receiptDate &&
                        receiptDate >=
                        filterDateFrom);

                const matchesDateTo =
                    !filterDateTo ||
                    (receiptDate &&
                        receiptDate <=
                        filterDateTo);

                return (
                    matchesSearch &&
                    matchesDateFrom &&
                    matchesDateTo
                );
            }
        );

    const displayedReceipts =
        showAllReceipts
            ? filteredReceipts
            : filteredReceipts.slice(
                0,
                8
            );

    /* =========================================================
       MONEY
    ========================================================= */

    const formatMoney = (
        amount,
        currencyCode = "KES"
    ) => {
        return new Intl.NumberFormat(
            "en-US",
            {
                style: "currency",
                currency: currencyCode
            }
        ).format(
            Number(amount || 0)
        );
    };

    /* =========================================================
       FORMAT DATE
    ========================================================= */

    const formatDate = (
        value
    ) => {
        if (!value) return "N/A";

        const date =
            new Date(
                `${value}`
            );

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return "N/A";
        }

        return date.toLocaleDateString(
            "en-US",
            {
                year: "numeric",
                month: "short",
                day: "numeric"
            }
        );
    };

    /* =========================================================
       CLEANUP
    ========================================================= */

    useEffect(() => {
        return () => {
            files.forEach(
                (item) => {
                    if (
                        item.preview
                    ) {
                        URL.revokeObjectURL(
                            item.preview
                        );
                    }
                }
            );
        };
    }, [files]);

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <div className="receipt-page">

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="receipt-page-header">

                <div>

                    <div className="receipt-eyebrow">
                        <Receipt size={16} />
                        FINANCIAL DOCUMENTS
                    </div>

                    <h1>
                        Receipt
                        <span>
                            {" "}Management
                        </span>
                    </h1>

                    <p>
                        Upload, organize and securely
                        manage your business receipts
                        in one place.
                    </p>

                </div>

                <div className="header-actions">
                    <button
                        type="button"
                        className="receipt-refresh-btn"
                        onClick={
                            fetchReceipts
                        }
                    >
                        <RefreshCw
                            size={18}
                        />

                        Refresh
                    </button>
                </div>

            </div>

            {/* =================================================
                UPLOAD AREA
            ================================================= */}

            <div className="receipt-upload-card">

                <div className="receipt-card-header">

                    <div className="receipt-card-icon">
                        <FileUp size={24} />
                    </div>

                    <div>
                        <h2>
                            Upload Receipts
                        </h2>

                        <p>
                            Upload multiple receipt
                            images or PDF documents
                            at once.
                        </p>
                    </div>

                </div>

                <form
                    onSubmit={
                        handleSubmit
                    }
                >

                    {/* =================================================
                        DROPZONE
                    ================================================= */}

                    <div
                        className={`receipt-dropzone ${dragActive
                            ? "drag-active"
                            : ""
                            }`}
                        onDragOver={
                            handleDragOver
                        }
                        onDragLeave={
                            handleDragLeave
                        }
                        onDrop={
                            handleDrop
                        }
                        onClick={() =>
                            fileInputRef.current?.click()
                        }
                    >

                        <input
                            ref={
                                fileInputRef
                            }
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.pdf"
                            multiple
                            onChange={
                                handleFileInput
                            }
                            hidden
                        />

                        <div className="dropzone-icon">
                            <Upload
                                size={34}
                            />
                        </div>

                        <h3>
                            Drop your receipts here
                        </h3>

                        <p>
                            or click to browse
                            multiple files
                        </p>

                        <span>
                            JPG, PNG, WEBP or PDF
                            · Maximum 10MB each
                        </span>

                    </div>

                    {/* =================================================
                        SELECTED FILES
                    ================================================= */}

                    {files.length > 0 && (

                        <div className="selected-files-section">

                            <div className="selected-files-header">

                                <div>
                                    <h3>
                                        Selected
                                        Receipts
                                    </h3>

                                    <span>
                                        {files.length}{" "}
                                        file
                                        {files.length !==
                                            1
                                            ? "s"
                                            : ""}{" "}
                                        ready
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    className="clear-files-btn"
                                    onClick={
                                        clearFiles
                                    }
                                >
                                    <X
                                        size={16}
                                    />

                                    Clear All
                                </button>

                            </div>

                            <div className="selected-files-list">

                                {files.map(
                                    (
                                        item,
                                        index
                                    ) => (

                                        <div
                                            className="selected-receipt-card"
                                            key={
                                                item.id
                                            }
                                        >

                                            <div className="selected-file-top">

                                                <div className="selected-file-preview">

                                                    {item.fileType ===
                                                        "image" ? (

                                                        <img
                                                            src={
                                                                item.preview
                                                            }
                                                            alt={
                                                                item.file.name
                                                            }
                                                        />

                                                    ) : (

                                                        <div className="selected-pdf-icon">
                                                            <FileText
                                                                size={34}
                                                            />
                                                        </div>

                                                    )}

                                                </div>

                                                <div className="selected-file-name">

                                                    <strong>
                                                        {index +
                                                            1}
                                                        .{" "}
                                                        {
                                                            item.file
                                                                .name
                                                        }
                                                    </strong>

                                                    <span>
                                                        {(
                                                            item.file
                                                                .size /
                                                            1024 /
                                                            1024
                                                        ).toFixed(
                                                            2
                                                        )}{" "}
                                                        MB
                                                    </span>

                                                </div>

                                                <button
                                                    type="button"
                                                    className="remove-selected-file"
                                                    onClick={() =>
                                                        removeSelectedFile(
                                                            item.id
                                                        )
                                                    }
                                                >
                                                    <X
                                                        size={18}
                                                    />
                                                </button>

                                            </div>

                                            <div className="receipt-form-grid">

                                                <div className="receipt-input-group">

                                                    <label>
                                                        <Building2
                                                            size={
                                                                15
                                                            }
                                                        />

                                                        Supplier /
                                                        Vendor
                                                    </label>

                                                    <input
                                                        type="text"
                                                        value={
                                                            item.supplier
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            updateFileMetadata(
                                                                item.id,
                                                                "supplier",
                                                                e
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        placeholder="Supplier name"
                                                    />

                                                </div>

                                                <div className="receipt-input-group">

                                                    <label>
                                                        <Receipt
                                                            size={
                                                                15
                                                            }
                                                        />

                                                        Receipt
                                                        Number
                                                    </label>

                                                    <input
                                                        type="text"
                                                        value={
                                                            item.receiptNumber
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            updateFileMetadata(
                                                                item.id,
                                                                "receiptNumber",
                                                                e
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        placeholder="REC-001245"
                                                    />

                                                </div>

                                                <div className="receipt-input-group">

                                                    <label>
                                                        <DollarSign
                                                            size={
                                                                15
                                                            }
                                                        />

                                                        Amount
                                                    </label>

                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={
                                                            item.amount
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            updateFileMetadata(
                                                                item.id,
                                                                "amount",
                                                                e
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        placeholder="0.00"
                                                    />

                                                </div>

                                                <div className="receipt-input-group">

                                                    <label>
                                                        <Calendar
                                                            size={
                                                                15
                                                            }
                                                        />

                                                        Receipt
                                                        Date
                                                    </label>

                                                    <input
                                                        type="date"
                                                        value={
                                                            item.receiptDate
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            updateFileMetadata(
                                                                item.id,
                                                                "receiptDate",
                                                                e
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                    />

                                                </div>

                                                <div className="receipt-input-group">

                                                    <label>
                                                        <DollarSign
                                                            size={
                                                                15
                                                            }
                                                        />

                                                        Currency
                                                    </label>

                                                    <select
                                                        value={
                                                            item.currency || "KES"
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            updateFileMetadata(
                                                                item.id,
                                                                "currency",
                                                                e
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                    >
                                                        {currencyOptions.map(
                                                            (curr) => (
                                                                <option
                                                                    key={
                                                                        curr.code
                                                                    }
                                                                    value={
                                                                        curr.code
                                                                    }
                                                                >
                                                                    {curr.code}{" "}
                                                                    (
                                                                    {
                                                                        curr.symbol
                                                                    }
                                                                    )
                                                                </option>
                                                            )
                                                        )}
                                                    </select>

                                                </div>

                                            </div>

                                            <div className="receipt-input-group">

                                                <label>
                                                    Description
                                                </label>

                                                <textarea
                                                    rows="3"
                                                    value={
                                                        item.description
                                                    }
                                                    onChange={(
                                                        e
                                                    ) =>
                                                        updateFileMetadata(
                                                            item.id,
                                                            "description",
                                                            e
                                                                .target
                                                                .value
                                                        )
                                                    }
                                                    placeholder="Description or notes..."
                                                />

                                            </div>

                                        </div>

                                    )
                                )}

                            </div>

                        </div>
                    )}

                    {/* =================================================
                        UPLOAD BUTTON
                    ================================================= */}

                    {files.length > 0 && (

                        <button
                            type="submit"
                            className="receipt-submit-btn"
                            disabled={
                                uploading
                            }
                        >

                            {uploading ? (

                                <>
                                    <Loader2
                                        size={20}
                                        className="spin"
                                    />

                                    Uploading{" "}
                                    {
                                        files.length
                                    }{" "}
                                    Receipts...
                                </>

                            ) : (

                                <>
                                    <Upload
                                        size={20}
                                    />

                                    Upload{" "}
                                    {
                                        files.length
                                    }{" "}
                                    Receipt
                                    {
                                        files.length !==
                                            1
                                            ? "s"
                                            : ""
                                    }
                                </>

                            )}

                        </button>

                    )}

                </form>

            </div>

            {/* =================================================
                RECEIPTS LIST
            ================================================= */}

            <div className="receipt-list-card">

                <div className="receipt-list-header">

                    <div>

                        <div className="receipt-section-title">

                            <div className="receipt-card-icon small">
                                <Receipt
                                    size={20}
                                />
                            </div>

                            <div>
                                <h2>
                                    Recent Receipts
                                </h2>

                                <p>
                                    {receipts.length}{" "}
                                    receipts
                                    available
                                </p>
                            </div>

                        </div>

                    </div>

                    <div className="receipt-list-actions">

                        <div className="receipt-search">

                            <Search
                                size={17}
                            />

                            <input
                                type="text"
                                placeholder="Search receipts..."
                                value={
                                    searchTerm
                                }
                                onChange={(
                                    e
                                ) =>
                                    setSearchTerm(
                                        e.target.value
                                    )
                                }
                            />

                        </div>

                        <div className="receipt-date-range">

                            <div className="receipt-date-input">

                                <Calendar size={17} />

                                <input
                                    type="date"
                                    value={filterDateFrom}
                                    max={filterDateTo || undefined}
                                    onChange={(e) =>
                                        setFilterDateFrom(
                                            e.target.value
                                        )
                                    }
                                    title="From date"
                                />

                            </div>

                            <span className="date-range-separator">
                                →
                            </span>

                            <div className="receipt-date-input">

                                <Calendar size={17} />

                                <input
                                    type="date"
                                    value={filterDateTo}
                                    min={filterDateFrom || undefined}
                                    onChange={(e) =>
                                        setFilterDateTo(
                                            e.target.value
                                        )
                                    }
                                    title="To date"
                                />

                            </div>

                            {(filterDateFrom || filterDateTo) && (
                                <button
                                    type="button"
                                    className="clear-date-filter"
                                    onClick={() => {
                                        setFilterDateFrom("");
                                        setFilterDateTo("");
                                    }}
                                    title="Clear date range"
                                >
                                    <X size={14} />
                                </button>
                            )}

                        </div>

                        <button
                            type="button"
                            className="view-all-btn"
                            onClick={() =>
                                setShowAllReceipts(
                                    (prev) =>
                                        !prev
                                )
                            }
                        >
                            {showAllReceipts
                                ? "Show Recent"
                                : "View All"}

                            {showAllReceipts ? (
                                <ChevronLeft
                                    size={17}
                                />
                            ) : (
                                <ChevronRight
                                    size={17}
                                />
                            )}
                        </button>

                    </div>

                </div>

                {/* =================================================
                    LIST
                ================================================= */}

                {loadingReceipts ? (

                    <div className="receipt-loading">

                        <Loader2
                            size={35}
                            className="spin"
                        />

                        <p>
                            Loading receipts...
                        </p>

                    </div>

                ) : displayedReceipts.length ===
                    0 ? (

                    <div className="receipt-empty">

                        <Receipt
                            size={50}
                        />

                        <h3>
                            No receipts found
                        </h3>

                        <p>
                            Upload your first receipt
                            to get started.
                        </p>

                    </div>

                ) : (

                    <div className="receipt-list">

                        {displayedReceipts.map(
                            (
                                receipt
                            ) => {

                                const fileUrl =
                                    getReceiptFileUrl(
                                        receipt
                                    );

                                const isImage =
                                    receipt.file_type ===
                                    "image" ||
                                    String(
                                        receipt.mime_type ||
                                        ""
                                    ).startsWith(
                                        "image/"
                                    );

                                const receiptCurrency = receipt.currency || "KES";

                                return (

                                    <div
                                        className="receipt-item"
                                        key={
                                            receipt.id
                                        }
                                    >

                                        {/* FILE */}

                                        <div className="receipt-file-icon">

                                            {isImage ? (
                                                <ImageIcon
                                                    size={22}
                                                />
                                            ) : (
                                                <FileText
                                                    size={22}
                                                />
                                            )}

                                        </div>

                                        {/* INFO */}

                                        <div className="receipt-item-info">

                                            <h3>
                                                {
                                                    receipt.supplier
                                                }
                                            </h3>

                                            <span>
                                                {receipt.receipt_number ||
                                                    "No receipt number"}
                                            </span>

                                            <small>
                                                {
                                                    receipt.file_name
                                                }
                                            </small>

                                        </div>

                                        {/* AMOUNT */}

                                        <div className="receipt-amount">

                                            <strong>
                                                {formatMoney(
                                                    receipt.amount,
                                                    receiptCurrency
                                                )}
                                            </strong>

                                            <span>
                                                {formatDate(
                                                    receipt.receipt_date
                                                )}
                                            </span>

                                            {receiptCurrency !== "KES" && (
                                                <span className="currency-badge">
                                                    {receiptCurrency}
                                                </span>
                                            )}

                                        </div>

                                        {/* ACTIONS */}

                                        <div className="receipt-actions">

                                            <button
                                                type="button"
                                                title="View Receipt"
                                                onClick={() =>
                                                    viewReceipt(
                                                        receipt
                                                    )
                                                }
                                            >
                                                <Eye
                                                    size={18}
                                                />
                                            </button>

                                            <button
                                                type="button"
                                                title="Edit Receipt"
                                                onClick={() =>
                                                    startEdit(
                                                        receipt
                                                    )
                                                }
                                            >
                                                <Edit3
                                                    size={18}
                                                />
                                            </button>

                                            <button
                                                type="button"
                                                title="Download"
                                                onClick={() =>
                                                    downloadReceipt(
                                                        receipt
                                                    )
                                                }
                                            >
                                                <Download
                                                    size={18}
                                                />
                                            </button>

                                            <button
                                                type="button"
                                                title="Delete Receipt"
                                                className="delete-btn"
                                                onClick={() =>
                                                    deleteReceipt(
                                                        receipt
                                                    )
                                                }
                                            >
                                                <Trash2
                                                    size={18}
                                                />
                                            </button>

                                        </div>

                                    </div>
                                );
                            }
                        )}

                    </div>
                )}

                {/* =================================================
                    SHOW ALL BUTTON
                ================================================= */}

                {!loadingReceipts &&
                    filteredReceipts.length >
                    8 && (

                        <div className="receipt-list-footer">

                            <button
                                type="button"
                                onClick={() =>
                                    setShowAllReceipts(
                                        (prev) =>
                                            !prev
                                    )
                                }
                            >
                                {showAllReceipts
                                    ? "Show Recent Receipts"
                                    : `View All ${filteredReceipts.length} Receipts`}

                                {showAllReceipts ? (
                                    <ChevronLeft
                                        size={18}
                                    />
                                ) : (
                                    <ChevronRight
                                        size={18}
                                    />
                                )}

                            </button>

                        </div>
                    )}

            </div>

            {/* =================================================
                EDIT MODAL
            ================================================= */}

            {editingReceipt && (

                <div
                    className="receipt-modal-backdrop"
                    onClick={() =>
                        setEditingReceipt(
                            null
                        )
                    }
                >

                    <div
                        className="receipt-edit-modal"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <div className="modal-header">

                            <div>
                                <span>
                                    RECEIPT MANAGEMENT
                                </span>

                                <h2>
                                    Edit Receipt
                                </h2>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setEditingReceipt(
                                        null
                                    )
                                }
                            >
                                <X
                                    size={21}
                                />
                            </button>

                        </div>

                        <div className="edit-file-name">

                            {editingReceipt.file_type ===
                                "image" ? (
                                <ImageIcon
                                    size={18}
                                />
                            ) : (
                                <FileText
                                    size={18}
                                />
                            )}

                            {
                                editingReceipt.file_name
                            }

                        </div>

                        <div className="receipt-form-grid">

                            <div className="receipt-input-group">

                                <label>
                                    <Building2
                                        size={15}
                                    />

                                    Supplier
                                </label>

                                <input
                                    type="text"
                                    value={
                                        editingReceipt.supplier
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setEditingReceipt(
                                            (
                                                prev
                                            ) => ({
                                                ...prev,
                                                supplier:
                                                    e
                                                        .target
                                                        .value
                                            })
                                        )
                                    }
                                />

                            </div>

                            <div className="receipt-input-group">

                                <label>
                                    <Receipt
                                        size={15}
                                    />

                                    Receipt Number
                                </label>

                                <input
                                    type="text"
                                    value={
                                        editingReceipt.receiptNumber
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setEditingReceipt(
                                            (
                                                prev
                                            ) => ({
                                                ...prev,
                                                receiptNumber:
                                                    e
                                                        .target
                                                        .value
                                            })
                                        )
                                    }
                                />

                            </div>

                            <div className="receipt-input-group">

                                <label>
                                    <DollarSign
                                        size={15}
                                    />

                                    Amount
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={
                                        editingReceipt.amount
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setEditingReceipt(
                                            (
                                                prev
                                            ) => ({
                                                ...prev,
                                                amount:
                                                    e
                                                        .target
                                                        .value
                                            })
                                        )
                                    }
                                />

                            </div>

                            <div className="receipt-input-group">

                                <label>
                                    <Calendar
                                        size={15}
                                    />

                                    Receipt Date
                                </label>

                                <input
                                    type="date"
                                    value={normalizeDate(
                                        editingReceipt.receiptDate
                                    )}
                                    onChange={(
                                        e
                                    ) =>
                                        setEditingReceipt(
                                            (
                                                prev
                                            ) => ({
                                                ...prev,
                                                receiptDate:
                                                    e
                                                        .target
                                                        .value
                                            })
                                        )
                                    }
                                />

                            </div>

                            <div className="receipt-input-group">

                                <label>
                                    <DollarSign
                                        size={15}
                                    />

                                    Currency
                                </label>

                                <select
                                    value={
                                        editingReceipt.currency || "KES"
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setEditingReceipt(
                                            (
                                                prev
                                            ) => ({
                                                ...prev,
                                                currency:
                                                    e
                                                        .target
                                                        .value
                                            })
                                        )
                                    }
                                >
                                    {currencyOptions.map(
                                        (curr) => (
                                            <option
                                                key={
                                                    curr.code
                                                }
                                                value={
                                                    curr.code
                                                }
                                            >
                                                {curr.code}{" "}
                                                (
                                                {
                                                    curr.symbol
                                                }
                                                )
                                            </option>
                                        )
                                    )}
                                </select>

                            </div>

                        </div>

                        <div className="receipt-input-group">

                            <label>
                                Description
                            </label>

                            <textarea
                                rows="5"
                                value={
                                    editingReceipt.description
                                }
                                onChange={(
                                    e
                                ) =>
                                    setEditingReceipt(
                                        (
                                            prev
                                        ) => ({
                                            ...prev,
                                            description:
                                                e
                                                    .target
                                                    .value
                                        })
                                    )
                                }
                            />

                        </div>

                        <div className="modal-actions">

                            <button
                                type="button"
                                className="modal-cancel"
                                onClick={() =>
                                    setEditingReceipt(
                                        null
                                    )
                                }
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="modal-save"
                                onClick={
                                    saveEdit
                                }
                                disabled={
                                    savingEdit
                                }
                            >

                                {savingEdit ? (
                                    <>
                                        <Loader2
                                            size={18}
                                            className="spin"
                                        />

                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save
                                            size={18}
                                        />

                                        Save Changes
                                    </>
                                )}

                            </button>

                        </div>

                    </div>

                </div>
            )}

            {/* =================================================
                VIEW MODAL
            ================================================= */}

            {viewingReceipt && (

                <div
                    className="receipt-view-backdrop"
                    onClick={() =>
                        setViewingReceipt(
                            null
                        )
                    }
                >

                    <div
                        className="receipt-view-modal"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <div className="view-modal-header">

                            <div>

                                <span>
                                    RECEIPT
                                </span>

                                <h2>
                                    {
                                        viewingReceipt.supplier
                                    }
                                </h2>

                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setViewingReceipt(
                                        null
                                    )
                                }
                            >
                                <X
                                    size={22}
                                />
                            </button>

                        </div>

                        <div className="receipt-document-viewer">

                            {getReceiptFileUrl(
                                viewingReceipt
                            ) ? (

                                viewingReceipt.file_type ===
                                    "image" ||
                                    String(
                                        viewingReceipt.mime_type ||
                                        ""
                                    ).startsWith(
                                        "image/"
                                    ) ? (

                                    <img
                                        src={getReceiptFileUrl(
                                            viewingReceipt
                                        )}
                                        alt={
                                            viewingReceipt.file_name ||
                                            "Receipt"
                                        }
                                    />

                                ) : (

                                    <iframe
                                        src={getReceiptFileUrl(
                                            viewingReceipt
                                        )}
                                        title="Receipt PDF"
                                    />

                                )

                            ) : (

                                <div className="viewer-error">

                                    <FileText
                                        size={50}
                                    />

                                    <p>
                                        Receipt file
                                        unavailable.
                                    </p>

                                </div>
                            )}

                        </div>

                        <div className="view-modal-footer">

                            <div>

                                <strong>
                                    Receipt Number:
                                </strong>

                                <span>
                                    {
                                        viewingReceipt.receipt_number ||
                                        "N/A"
                                    }
                                </span>

                            </div>

                            <div>

                                <strong>
                                    Amount:
                                </strong>

                                <span>
                                    {formatMoney(
                                        viewingReceipt.amount,
                                        viewingReceipt.currency || "KES"
                                    )}
                                </span>

                            </div>

                            <div>

                                <strong>
                                    Currency:
                                </strong>

                                <span>
                                    {viewingReceipt.currency || "KES"}
                                </span>

                            </div>

                            <div>

                                <strong>
                                    Date:
                                </strong>

                                <span>
                                    {formatDate(
                                        viewingReceipt.receipt_date
                                    )}
                                </span>

                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    downloadReceipt(
                                        viewingReceipt
                                    )
                                }
                            >
                                <Download
                                    size={17}
                                />

                                Open File
                            </button>

                        </div>

                    </div>

                </div>
            )}

        </div>
    );
};

export default ReceiptUpload;